/**
 * Russian catalog (Cyrillic) for the Phase 2 `MessageId` set — Agent 9, batch 1.
 *
 * `dir: 'ltr'`. Native-grade Russian at a mathematician's register. **Warrant → «Обоснование»**
 * (the epistemic-justification reading; Toulmin's «основание/гарантия» is the flagged
 * alternative). `scriptOf('ru')` → cyrillic, paired with the Cyrillic Noto-led font stack.
 *
 * W/M grades stay Latin (`::numbering-system/latn`) — a grade is a stable id, not prose.
 * Russian needs ALL FOUR CLDR cardinal categories — one / few / many / other — and the noun
 * «ограничение» (neuter) is declined to agree in each (1 ограничение · 2 ограничения ·
 * 5 ограничений), so `frameActive` reads correctly at every count (plus an explicit `=0`).
 * Russian also runs long; the layout test confirms the longest strings still lay out cleanly.
 * Per INV-STABLE-ID a wrong translation is a presentation concern only — verdicts stay fixed.
 */
import { P2 } from '../message-ids-phase2.js';
export const RU_CATALOG = {
    locale: 'ru',
    dir: 'ltr',
    messages: {
        [P2.inquiryTitle]: 'Исследование',
        [P2.sectionClaims]: 'Утверждения',
        [P2.sectionIntegrity]: 'Целостность',
        [P2.axisWarrantLabel]: 'Обоснование',
        [P2.axisWarrantValue]: 'W{w, number, ::numbering-system/latn}',
        [P2.axisEmpiricalLabel]: 'Эмпирическое',
        [P2.axisEmpiricalValue]: '{kind, select, defn {Определение} interp {Интерпретация} testable {Проверяемое} tested {Проверенное} refuted {Опровергнутое} program {Исследовательская программа} philos {Философское} other {Неизвестно}}',
        [P2.axisModalLabel]: 'Модальное',
        [P2.axisModalValue]: 'M{m, number, ::numbering-system/latn}',
        [P2.axisKidSummary]: 'Уровень силы {w, number, ::numbering-system/latn}',
        [P2.stateExplorable]: 'Исследуемое',
        [P2.stateRefuted]: 'Опровергнуто',
        [P2.stateOutside]: 'Вне области рассмотрения',
        [P2.verdictConflict]: 'Конфликтует с {constraints}',
        [P2.verdictOutside]: 'Требуется {missing}',
        [P2.lossRegisterPlain]: 'Упрощено до обычного языка — часть точности опущена.',
        [P2.lossRegisterKid]: 'Упрощено для юных читателей — многие детали опущены.',
        [P2.lossProfileDetail]: 'Полный профиль «Обоснование / Эмпирическое / Модальное» кратко изложен здесь.',
        [P2.lossConflictDetail]: 'Подробности конфликта в этом представлении сокращены.',
        [P2.laneVerified]: 'Проверено',
        [P2.laneUnverified]: 'Не проверено',
        [P2.laneConflict]: 'Конфликт доказан',
        [P2.constraintLogic]: 'Логика',
        [P2.constraintLightBends]: 'Свет искривляется в гравитационном поле',
        [P2.constraintParallelPostulate]: 'Постулат о параллельных',
        [P2.claimScalarGravity]: 'Скалярная теория гравитации',
        [P2.claimNonEuclidean]: 'Неевклидова геометрия',
        [P2.claimSubstrateSr]: 'Субстратная интерпретация специальной теории относительности',
        [P2.edgeExtObservation]: 'Внешнее наблюдение',
        [P2.edgeResourceSample]: 'Общий вычислительный ресурс',
        [P2.edgeHeuristicSample]: 'Эвристическое руководство',
        [P2.edgeSharedSample]: 'Общее основание',
        // --- Phase 2 view-chrome ---
        [P2.sectionConstraints]: 'Ограничения',
        [P2.sectionEdges]: 'Рёбра',
        [P2.sectionFrame]: 'Рамка',
        [P2.sectionExposure]: 'Подверженность',
        [P2.statusAdoptedFormal]: 'Принято (формально)',
        [P2.statusAdoptedInterpretive]: 'Принято (интерпретативно)',
        [P2.statusSuspended]: 'Приостановлено',
        [P2.edgeEffectConstraint]: 'Образует ограничение (может опровергнуть)',
        [P2.edgeEffectResource]: 'Ресурс — не влияет на вердикты',
        [P2.edgeEffectHeuristic]: 'Эвристика — не влияет на вердикты',
        [P2.edgeEffectShared]: 'Общее основание — не влияет на вердикты',
        [P2.frameActive]: '{count, plural, =0 {Нет активных ограничений} one {# активное ограничение} few {# активных ограничения} many {# активных ограничений} other {# активных ограничения}}',
        [P2.exposureNone]: 'Нет интерпретативных зависимостей',
        [P2.exposureDep]: 'Зависит от {id}',
        [P2.laneTrackMachine]: 'Машина',
        [P2.laneTrackExpert]: 'Эксперт',
        [P2.laneTrackCommunity]: 'Сообщество',
        [P2.expertAudited]: 'Аудит пройден',
        [P2.expertInProgress]: 'В процессе',
        [P2.expertNotReviewed]: 'Не рассмотрено',
        [P2.communityFlags]: 'Есть отметки',
        [P2.communityNone]: 'Нет отметок',
        [P2.controlChallenge]: 'Оспорить',
        [P2.controlActivate]: 'Активировать',
        [P2.controlMakeInterpretive]: 'Сделать интерпретативным',
        [P2.controlImportEdge]: 'Импортировать ребро',
        [P2.controlPromote]: 'Повысить',
        [P2.controlUndo]: 'Отменить',
        [P2.controlRedo]: 'Вернуть',
        [P2.controlRegister]: 'Зарегистрировать',
        [P2.promoteRejected]: 'Повышение отклонено',
        [P2.promoteAccepted]: 'Повышение принято',
        [P2.promoteReasonRefutationTerminal]: 'Доказательство не может исцелить опровержение.',
        [P2.promoteReasonKeystone]: 'Повышение до краеугольного требует эмпирической поддержки.',
        [P2.promoteReasonEmpiricalNotFixed]: 'Эмпирический вид не зафиксирован.',
        [P2.promoteReasonModalNotFixed]: 'Модальная степень не зафиксирована.',
        [P2.promoteReasonWarrantNotImproved]: 'Степень обоснования не улучшена.',
        [P2.evidenceComputation]: "{engine} вычислил {op} = {result} при {config}",
        [P2.lossNumericRounding]: "Результат округлён до {dp} знаков после запятой",
    },
};
//# sourceMappingURL=ru.js.map